// ── Server List API ─────────────────────────────────────────────────────────
// Fetches the full server list from the PlayCDU public API.
// Each entry includes modpackIndexId (= MPI ID used by Apogee), FQDN, etc.

export interface ServerListEntry {
  serverId: number;
  serverAddress: string;
  clusterName: string;
  regionName: string;
  modpackTitle: string;
  modpackVersion: string;
  modpackAuthor: string | null;
  modpackLauncher: string | null;
  modpackDownloadUrl: string | null;
  modpackIndexId: number | null;
  minecraftVersion: string | null;
  thumbnailUrl: string | null;
  isServerOnline: boolean;
  onlinePlayerCount: number;
  maxPlayerCount: number;
  clusterOnlinePlayerCount: number;
  clusterParentServerId: number | null;
  dateAdded: number;
  timestamp: string;
  /** Present on the wire for the parent server too; the type historically left them off. */
  lastOnlineTime?: string | null;
  lastOfflineTime?: string | null;
  clusterChildren?: ServerListClusterChild[];
}

export interface ServerListClusterChild {
  serverAddress: string;
  regionName: string;
  isServerOnline: boolean;
  onlinePlayerCount: number;
  maxPlayerCount: number;
  lastOnlineTime: string | null;
  lastOfflineTime: string | null;
  specialInstructions: string | null;
  onlinePlayerList: { name: string }[];
}

const API_URL = 'https://api.playcdu.co/server-list';

/** In-memory cache with a 5-minute TTL */
let cachedData: ServerListEntry[] | null = null;
let cachedAt = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

class ServerListApiService {
  /** `maxAge` shortens the cache for callers that watch liveness rather than identity. */
  async getServerList(options: { maxAge?: number } = {}): Promise<ServerListEntry[]> {
    const now = Date.now();
    if (cachedData && now - cachedAt < Math.min(CACHE_TTL, options.maxAge ?? CACHE_TTL)) {
      return cachedData;
    }

    const response = await fetch(API_URL);
    if (!response.ok) {
      throw new Error(`Server List API Error: ${response.status}`);
    }

    const data: ServerListEntry[] = await response.json();
    cachedData = data;
    cachedAt = now;
    return data;
  }

  /** Find all CDU servers running a pack with the given MPI ID */
  async getServersByMpiId(mpiId: number): Promise<ServerListEntry[]> {
    const servers = await this.getServerList();
    return servers.filter((s) => s.modpackIndexId === mpiId);
  }

  /** Find the server entry matching a specific FQDN / server address */
  async getServerByFqdn(fqdn: string): Promise<ServerListEntry | undefined> {
    const servers = await this.getServerList();
    const normalized = fqdn.toLowerCase();
    return servers.find((s) => {
      if (s.serverAddress.toLowerCase() === normalized) return true;
      // Also check cluster children
      return s.clusterChildren?.some((c) => c.serverAddress.toLowerCase() === normalized);
    });
  }

  /** Get the MPI ID for a given FQDN (useful for enrichment lookups) */
  async getMpiIdForFqdn(fqdn: string): Promise<number | null> {
    const server = await this.getServerByFqdn(fqdn);
    return server?.modpackIndexId ?? null;
  }

  /** Build a map of MPI ID → server entries */
  async getMpiIdToServersMap(): Promise<Map<number, ServerListEntry[]>> {
    const servers = await this.getServerList();
    const map = new Map<number, ServerListEntry[]>();
    for (const s of servers) {
      if (s.modpackIndexId) {
        const existing = map.get(s.modpackIndexId) || [];
        existing.push(s);
        map.set(s.modpackIndexId, existing);
      }
    }
    return map;
  }
}

export const serverListApiService = new ServerListApiService();
